import { createStore } from 'vuex'
import * as api from '@/services/api' // Assuming api service functions are defined here
import router from '@/router'

export default createStore({
  state: {
    user: null,
    posts: [], // Holds published posts for lists/feeds
    currentPost: null, // Holds the single post being viewed (published)
    currentEditingItem: null, // Holds { post: {...}, currentDraft: {...}, draftVersions: [] }
    relatedPosts: [],
    token: localStorage.getItem('token') || null,
    loading: false,
    error: null,
    dashboardPosts: [], // Holds posts/drafts specifically for the dashboard view
    tags: [], // Holds tags with dynamic counts
    suggestedTags: [],
    userLikes: [],
    userBookmarks: [],
  },
  mutations: {
    // --- Dashboard ---
    setDashboardPosts(state, posts) {
      // Ensure viewCount is present, similar to setPosts
      state.dashboardPosts = posts.map(p => ({ ...p, viewCount: p.viewCount || 0 }));
    },

    // --- User & Auth ---
    setUser(state, user) {
      state.user = user
    },
    setToken(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    clearToken(state) {
      state.token = null
      localStorage.removeItem('token')
    },
    setUserLikes(state, likes) {
      state.userLikes = likes || []
    },
    setUserBookmarks(state, bookmarks) {
      state.userBookmarks = bookmarks || []
    },
    addUserLike(state, postId) {
      if (!state.userLikes.includes(postId)) {
        state.userLikes.push(postId)
      }
      // Update like count/status in relevant state parts
      if (state.currentPost && state.currentPost._id === postId) {
        state.currentPost.likes = (state.currentPost.likes || 0) + 1
        state.currentPost.isLiked = true
      }
      const postInList = state.posts.find(p => p._id === postId)
      if (postInList) {
        postInList.likes = (postInList.likes || 0) + 1
        postInList.isLiked = true
      }
    },
    removeUserLike(state, postId) {
      state.userLikes = state.userLikes.filter(id => id !== postId)
       // Update like count/status in relevant state parts
      if (state.currentPost && state.currentPost._id === postId) {
        state.currentPost.likes = Math.max((state.currentPost.likes || 0) - 1, 0)
        state.currentPost.isLiked = false
      }
      const postInList = state.posts.find(p => p._id === postId)
      if (postInList) {
        postInList.likes = Math.max((postInList.likes || 0) - 1, 0)
        postInList.isLiked = false
      }
    },
    addUserBookmark(state, postId) {
      if (!state.userBookmarks.includes(postId)) {
        state.userBookmarks.push(postId)
      }
       // Update bookmark status in relevant state parts
      if (state.currentPost && state.currentPost._id === postId) {
        state.currentPost.isBookmarked = true
      }
      const postInList = state.posts.find(p => p._id === postId)
      if (postInList) {
        postInList.isBookmarked = true
      }
    },
    removeUserBookmark(state, postId) {
      state.userBookmarks = state.userBookmarks.filter(id => id !== postId)
      // Update bookmark status in relevant state parts
      if (state.currentPost && state.currentPost._id === postId) {
        state.currentPost.isBookmarked = false
      }
      const postInList = state.posts.find(p => p._id === postId)
      if (postInList) {
        postInList.isBookmarked = false
      }
    },

    // --- Posts & Content ---
    setPosts(state, posts) {
       // Ensure viewCount is present
      state.posts = posts.map(p => ({ ...p, viewCount: p.viewCount || 0 }))
    },
    setCurrentPost(state, post) {
       // Ensure viewCount is present
      state.currentPost = post ? { ...post, viewCount: post.viewCount || 0 } : null
    },
     setRelatedPosts(state, posts) {
       // Ensure viewCount is present
      state.relatedPosts = posts.map(p => ({ ...p, viewCount: p.viewCount || 0 }))
    },
    // addPost might not be needed if createPost doesn't add to list directly
    // addPost(state, post) { state.posts.unshift(post) }, 
    updatePost(state, updatedPost) {
      // Use _id for MongoDB consistency
      const index = state.posts.findIndex(post => post._id === updatedPost._id) 
      if (index !== -1) {
         // If status changed to unpublished (isDraft=true), remove it from the main list
         if (updatedPost.isDraft) {
             state.posts.splice(index, 1);
         } else {
             // Update item in the list (ensure viewCount is included) and re-sort
             const itemWithViewCount = { ...updatedPost, viewCount: updatedPost.viewCount || state.posts[index].viewCount || 0 };
             state.posts.splice(index, 1, itemWithViewCount);
             // Re-sort based on publishedAt or createdAt? Assuming publishedAt for published list
             state.posts.sort((a, b) => new Date(b.publishedAt || b.createdAt || 0) - new Date(a.publishedAt || a.createdAt || 0));
         }
      } else if (!updatedPost.isDraft) {
          // If it wasn't in the list but is now published, add it
          const itemWithViewCount = { ...updatedPost, viewCount: updatedPost.viewCount || 0 };
          state.posts.push(itemWithViewCount);
          state.posts.sort((a, b) => new Date(b.publishedAt || b.createdAt || 0) - new Date(a.publishedAt || a.createdAt || 0));
      }
      // Update currentPost if it's the one being updated (ensure viewCount)
      if (state.currentPost && state.currentPost._id === updatedPost._id) {
        state.currentPost = { ...state.currentPost, ...updatedPost, viewCount: updatedPost.viewCount || state.currentPost.viewCount || 0 };
      }
       // Also update the post part if it's the currently edited item
      if (state.currentEditingItem && state.currentEditingItem.post._id === updatedPost._id) {
          state.currentEditingItem.post = { ...state.currentEditingItem.post, ...updatedPost, viewCount: updatedPost.viewCount || state.currentEditingItem.post.viewCount || 0 };
      }
    },
     deletePost(state, postId) {
      state.posts = state.posts.filter(post => post._id !== postId) // Use _id for MongoDB
      if (state.currentPost && state.currentPost._id === postId) {
        state.currentPost = null
      }
       // Also clear editing state if the deleted item was being edited
      if (state.currentEditingItem && state.currentEditingItem.post._id === postId) {
          state.currentEditingItem = null;
      }
    },
     addComment(state, { postId, comment }) {
      // Note: Comments might need adjustment if not directly on Post schema anymore
      if (state.currentPost && state.currentPost._id === postId) {
        state.currentPost.comments = state.currentPost.comments || []; // Ensure comments array exists
        state.currentPost.comments.push(comment)
      }
      const post = state.posts.find(p => p._id === postId) // Use _id
      if (post) {
         post.comments = post.comments || []; // Ensure comments array exists
        post.comments.push(comment)
      }
    },

    // --- Draft Management ---
    setCurrentEditingItem(state, { post, drafts }) {
        // Ensure viewCount is present on the post part
        const postWithViewCount = { ...post, viewCount: post.viewCount || 0 };
        // Sort drafts by creation date, newest first
        const sortedDrafts = drafts && Array.isArray(drafts) 
            ? [...drafts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) 
            : [];
            
        const latestDraft = sortedDrafts.length > 0 ? sortedDrafts[0] : null;

        state.currentEditingItem = {
            post: postWithViewCount,
            // Set currentDraft to the latest one based on time
            currentDraft: latestDraft, 
            // All other drafts are considered versions (excluding the latest one)
            draftVersions: sortedDrafts.slice(1), 
        };
        console.log('STORE: setCurrentEditingItem - Post:', postWithViewCount);
        console.log('STORE: setCurrentEditingItem - Latest Draft:', latestDraft);
        console.log('STORE: setCurrentEditingItem - Draft Versions:', sortedDrafts.slice(1));
        
        // Update post in main list if it exists there
        const index = state.posts.findIndex(p => p._id === post._id); // Use _id
        if (index !== -1) {
            // Ensure we update with the version that includes viewCount
            state.posts.splice(index, 1, postWithViewCount); 
        }
    },
    clearCurrentEditingItem(state) {
        state.currentEditingItem = null;
    },
    updateCurrentDraft(state, updatedDraftData) {
        // Updates the content of the current draft in the editing state
        if (state.currentEditingItem && state.currentEditingItem.currentDraft) {
            // Merge new data into the existing current draft object
            state.currentEditingItem.currentDraft = { 
                ...state.currentEditingItem.currentDraft, 
                ...updatedDraftData 
            };
        }
    },
    addDraftVersion(state, versionedDraft) {
        // Adds a newly versioned draft to the list of versions
        if (state.currentEditingItem) {
             state.currentEditingItem.draftVersions = state.currentEditingItem.draftVersions || []; // Ensure array exists
            state.currentEditingItem.draftVersions.unshift(versionedDraft); // Add to beginning (newest)
        }
    },
    removeOldestDraftVersion(state) {
        // Removes the oldest draft version (if needed after limit enforcement)
        if (state.currentEditingItem && state.currentEditingItem.draftVersions && state.currentEditingItem.draftVersions.length > 0) {
             state.currentEditingItem.draftVersions.pop(); // Remove the last element (oldest)
        }
    },
    setNewCurrentDraft(state, newCurrentDraft) {
         // Sets the new current draft after a version save
         if (state.currentEditingItem) {
            state.currentEditingItem.currentDraft = newCurrentDraft;
        }
    },
     incrementLocalViewCount(state, postId) {
        // Optimistically increment view count in the main list
        const itemInList = state.posts.find(item => item._id === postId); // Use _id
        if (itemInList) {
            itemInList.viewCount = (itemInList.viewCount || 0) + 1;
        }
        // Optimistically increment view count in the currently viewed post
        if (state.currentPost && state.currentPost._id === postId) {
            state.currentPost.viewCount = (state.currentPost.viewCount || 0) + 1;
        }
         // Optimistically increment view count in the editing state
        if (state.currentEditingItem && state.currentEditingItem.post._id === postId) {
            state.currentEditingItem.post.viewCount = (state.currentEditingItem.post.viewCount || 0) + 1;
        }
    },

    // --- Tags ---
    setTags(state, tags) {
      state.tags = tags
    },
    setSuggestedTags(state, tags) {
      state.suggestedTags = tags
    },

    // --- General ---
    setLoading(state, isLoading) {
      state.loading = isLoading
    },
    setError(state, error) {
      state.error = error
    },
  },
  actions: {
    // --- Auth Actions ---
    async initializeAuth({ commit, state, dispatch }) {
      if (state.token) {
        try {
          const response = await api.getUserProfile() // Assumes api.js maps this
          commit('setUser', response)
          await dispatch('fetchUserLikes')
          await dispatch('fetchUserBookmarks')
        } catch (error) {
          console.error('Failed to fetch user profile:', error)
          if (error.response && error.response.status === 401) {
            commit('clearToken')
            commit('setUser', null)
          }
        }
      }
    },
    async signup({ commit, dispatch }, userData) {
      try {
        const response = await api.signup(userData) // Assumes api.js maps this
        commit('setUser', response.user)
        commit('setToken', response.token)
        await dispatch('fetchUserLikes')
        await dispatch('fetchUserBookmarks')
        router.push('/dashboard')
      } catch (error) {
        console.error('Signup failed:', error)
        throw error
      }
    },
    async signin({ commit, dispatch }, credentials) {
      try {
        const response = await api.login(credentials) // Assumes api.js maps this
        commit('setUser', response.user)
        commit('setToken', response.token)
        await dispatch('fetchUserLikes')
        await dispatch('fetchUserBookmarks')
        router.push('/dashboard')
      } catch (error) {
        console.error('Signin failed:', error)
        throw error
      }
    },
    logout({ commit }) {
      commit('setUser', null)
      commit('clearToken')
      commit('setPosts', [])
      commit('setUserLikes', [])
      commit('setUserBookmarks', [])
      commit('clearCurrentEditingItem') // Clear editing state on logout
      router.push('/')
    },

     // --- Post & Draft Actions ---
    async fetchUserPosts({ commit, state }, type = 'all') {
      // Fetches user's posts (published or drafts) for dashboard
      try {
        commit('setLoading', true);
        commit('setError', null);
        
        console.log('Fetching user posts with type:', type);
        
        const posts = await api.getUserPosts(type); // Assumes api.js maps this
        if (!Array.isArray(posts)) {
          console.error('Invalid response format for user posts:', posts);
          throw new Error('Invalid response format');
        }
        
        console.log(`Received ${posts.length} posts from API`);
        
        // Process posts to add like/bookmark status
        const processedPosts = posts.map(post => {
          if (post && post._id) {
            return {
              ...post,
              isLiked: state.userLikes.includes(post._id),
              isBookmarked: state.userBookmarks.includes(post._id),
              // Ensure id field exists for frontend components
              id: post._id || post.id
            };
          }
          return post;
        });
        
        console.log(`STORE: Committing ${processedPosts.length} valid posts/drafts to state.dashboardPosts.`);
        // Commit to the new dashboardPosts state
        commit('setDashboardPosts', processedPosts); 
        return processedPosts;
      } catch (error) {
        console.error('Error fetching user posts:', error);
        console.error('STORE: Error in fetchUserPosts action:', error); // Added STORE prefix
        commit('setError', 'Failed to fetch dashboard posts'); // More specific error
        commit('setDashboardPosts', []); // Clear dashboard posts on error
        return []; // Return empty array on error
      } finally {
        commit('setLoading', false);
      }
    },
    // Update fetchPosts action to accept and pass the tag parameter
    async fetchPosts({ commit, state }, { page, perPage, includeFeatured = false, tag = null }) { 
      // Fetches published posts for public lists
      if (!page || !perPage) {
        console.error('Invalid parameters:', { page, perPage })
        return null
      }
      
      if (!state.loading) {
        commit('setLoading', true)
      }
      commit('setError', null)
      try {
        // Pass the tag to the API call
        const response = await api.getPosts(page, perPage, includeFeatured, tag) // Pass tag to API

        // Validate the structure of the response
        if (!response || typeof response !== 'object' || !response.pagination) {
          console.error('STORE: Invalid response structure received from API:', response);
          throw new Error('Invalid response format from API');
        }
        
        // Ensure posts and topPosts are arrays, even if missing in response
        const apiPosts = Array.isArray(response.posts) ? response.posts : [];
        const apiTopPosts = Array.isArray(response.topPosts) ? response.topPosts : [];
        
        // Process top posts
        const topPosts = apiTopPosts.map(post => {
          if (!post || !post.id) { // Use 'id' from transformPost
             console.warn('STORE: Invalid top post data:', post); 
             return null; 
          }
          return {
            ...post,
            isLiked: state.userLikes.includes(post.id),
            isBookmarked: state.userBookmarks.includes(post.id)
          };
        }).filter(Boolean); // Remove nulls
        
        // Process regular posts
        const validPosts = apiPosts.map(post => {
           if (!post || !post.id) { // Use 'id' from transformPost
             console.warn('STORE: Invalid post data:', post); 
             return null; 
           }
           return {
             ...post,
             isLiked: state.userLikes.includes(post.id),
             isBookmarked: state.userBookmarks.includes(post.id)
           };
        }).filter(Boolean); // Remove nulls
        
        commit('setPosts', validPosts) // Commit only the paginated posts to the main list
        
        // Return the processed response for the component to use
        return {
          topPosts: topPosts,
          posts: validPosts,
          pagination: response.pagination
        };
      } catch (error) {
        console.error('STORE: Error in fetchPosts action:', error);
        commit('setError', 'Failed to fetch posts. Please try again later.');
        commit('setPosts', []); // Clear posts on error
        return null; // Return null to indicate failure
      } finally {
        if (state.loading) {
          commit('setLoading', false);
        }
      }
    },
    async fetchPost({ commit, state }, id) {
      // Fetches a single published post for viewing
      commit('setLoading', true)
      commit('setError', null)
      try {
        const post = await api.getPost(id) // Assumes api.js maps this
        if (post) {
          post.isLiked = state.userLikes.includes(post._id) // Use _id
          post.isBookmarked = state.userBookmarks.includes(post._id) // Use _id
        }
        commit('setCurrentPost', post)
        return post
      } catch (error) {
        console.error('Error fetching post:', error)
        commit('setError', 'Failed to fetch the post. Please try again later.')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
     async fetchRelatedPosts({ commit, state }, postId) {
      try {
        const relatedPosts = await api.getRelatedPosts(postId) // Assumes api.js maps this
        relatedPosts.forEach(post => {
          if (post && post._id) { // Use _id
            post.isLiked = state.userLikes.includes(post._id) // Use _id
            post.isBookmarked = state.userBookmarks.includes(post._id) // Use _id
          }
        })
        commit('setRelatedPosts', relatedPosts)
      } catch (error) {
        console.error('Error fetching related posts:', error)
        commit('setError', 'Failed to fetch related posts. Please try again later.')
      }
    },
    // Creates a new Post and its initial Draft
    async createPost({ commit }, initialDraftData) {
      commit('setLoading', true);
      commit('setError', null);
      try {
        // API returns { postId, initialDraftId }
        const response = await api.createPost(initialDraftData); // Assumes api.js maps this
        return response.postId; 
      } catch (error) {
        console.error('Error creating post:', error);
        commit('setError', error.response?.data?.message || 'Failed to create post.');
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
     // Auto-saves the current draft
    async autoSaveCurrentDraft({ commit, state }, draftData) {
       
       // Check if we have a postId in the draftData
       if (draftData && draftData._id) {
           const postId = draftData._id;
           delete draftData._id; // Remove _id from draftData before sending to API
           
           commit('setError', null);
           try {
               commit('updateCurrentDraft', draftData);
               await api.updateCurrentDraft(postId, draftData);
           } catch (error) {
               console.error(`STORE: Error auto-saving draft for post ${postId}:`, error);
               commit('setError', error.response?.data?.message || 'Auto-save failed.');
           }
           return;
       }
       
       // If no postId in draftData, try to get it from currentEditingItem
       if (!state.currentEditingItem) {
            // The createPost action in EditPost.vue will handle creating the post first
            return; 
        }
        
        if (!state.currentEditingItem.post || !state.currentEditingItem.post._id) {
            console.error('STORE: currentEditingItem.post._id is undefined, cannot auto-save');
            return;
        }
        
        const postId = state.currentEditingItem.post._id;
        commit('setError', null); 
        try {
            commit('updateCurrentDraft', draftData); 
            await api.updateCurrentDraft(postId, draftData); // Assumes api.js maps this
        } catch (error) {
             console.error(`STORE: Error auto-saving draft for post ${postId}:`, error);
             commit('setError', error.response?.data?.message || 'Auto-save failed.');
        }
    },
     // Saves the current state as a new draft version (manual save)
    async saveDraftVersion({ commit }, { postId, draftData }) { // Removed unused 'state' from parameters
        
        if (!postId) {
            console.error('Cannot save draft version: no post ID provided');
            commit('setError', 'Cannot save draft version: no post ID available');
            return null; // Return null or throw error
        }
        
        if (!draftData) {
             console.error('Cannot save draft version: no draft data provided');
             commit('setError', 'Cannot save draft version: no draft data available');
             return null; // Return null or throw error
        }

        commit('setLoading', true);
        commit('setError', null);
        try {
            // Call the updated API service function which now handles the PUT request
            // The API returns the newly saved draft object
            const savedDraft = await api.saveDraftVersion(postId, draftData); 

            if (!savedDraft) {
                throw new Error('API did not return the saved draft.');
            }

            // Update the current draft in the store with the response from the API
            commit('updateCurrentDraft', savedDraft); 
            
            // Optionally, add this saved state as a "version" if the backend 
            // distinguishes between auto-save updates and explicit version saves.
            // If PUT /api/drafts/:postId/current *always* creates a new version, 
            // we might need to refetch the draft list or update it here.
            // For now, assume updateCurrentDraft is sufficient.
            // commit('addDraftVersion', { ...savedDraft, isCurrent: false }); // Example if needed

            return savedDraft; // Return the saved draft object
        } catch (error) {
            console.error(`STORE: Error saving draft version for post ${postId}:`, error);
            commit('setError', error.response?.data?.message || 'Failed to save draft.');
            return null; // Return null on failure
        } finally {
            commit('setLoading', false);
        }
    },
     // Publishes a specific draft
    async publishPost({ commit }, { postId, draftId }) {
      commit('setLoading', true);
      commit('setError', null);
      try {
        if (!postId || !draftId) {
          throw new Error('Post ID and Draft ID are required to publish');
        }
        const publishedPost = await api.publishPost(postId, draftId); // Assumes api.js maps this
        commit('updatePost', publishedPost); 
        // commit('clearCurrentEditingItem'); // Optionally clear editing state
        return publishedPost;
      } catch (error) {
        console.error('Error publishing post:', error);
        commit('setError', error.response?.data?.message || 'Failed to publish post.');
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },
     // Fetches Post details and all its Drafts for editing
    async fetchPostForEdit({ commit }, postId) {
        commit('setLoading', true);
        commit('setError', null);
        commit('clearCurrentEditingItem'); 
        let postDetails = null;
        let drafts = [];
        try {
            // Attempt to fetch published post details first (might fail if never published)
            try {
                console.log(`STORE: Attempting to fetch published post details for ${postId}`);
                postDetails = await api.getPost(postId); 
                console.log(`STORE: Found published post details for ${postId}`);
            } catch (postError) {
                if (postError.response && postError.response.status === 404) {
                    console.warn(`STORE: Published post ${postId} not found, proceeding to fetch drafts.`);
                    // Initialize postDetails minimally if not found, 
                    // drafts will provide necessary info like title. Author might be missing.
                    postDetails = { _id: postId, title: 'Loading Draft...', content: '', isDraftPlaceholder: true }; // Add a flag
                } else {
                    // Rethrow other errors related to fetching post details
                    console.error(`STORE: Error fetching post details for ${postId}:`, postError);
                    throw postError; 
                }
            }

            // Fetch all drafts for this post ID
            console.log(`STORE: Fetching drafts for ${postId}`);
            drafts = await api.getDrafts(postId); 
            console.log(`STORE: Found ${drafts ? drafts.length : 0} drafts for ${postId}`);

            // FIX: Handle case where post exists but has no drafts
            if (!drafts || drafts.length === 0) {
                if (postDetails && !postDetails.isDraftPlaceholder) {
                    // Create a synthetic draft from the published post content
                    const syntheticDraft = {
                        postId: postDetails._id,
                        title: postDetails.title,
                        content: postDetails.content,
                        tags: postDetails.tags || [],
                        createdAt: new Date(), // Use current time as creation time for this temporary draft
                        _id: null // Indicate it's not a real saved draft from the DB yet
                    };
                    drafts = [syntheticDraft]; // Use this synthetic draft
                    console.log(`STORE: No existing drafts found for ${postId}. Created synthetic draft from published post.`);
                } else {
                    // If postDetails also wasn't found or was just a placeholder, then error is valid
                    throw new Error(`No drafts found and no published content available for post ID ${postId}. Cannot edit.`);
                }
            }
            
            // If postDetails was minimal (404 case), populate from latest draft
            if (postDetails.isDraftPlaceholder) {
                // Sort drafts by createdAt descending to find the latest
                const latestDraft = drafts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
                if (latestDraft) {
                    console.log(`STORE: Populating title from latest draft for ${postId}`);
                    postDetails.title = latestDraft.title; 
                    // Note: We still might lack author, image, featured status if never published
                }
                delete postDetails.isDraftPlaceholder; // Remove the temporary flag
            }

            // Commit the potentially partial post details and the fetched drafts
            console.log(`STORE: Committing setCurrentEditingItem for ${postId}`);
            commit('setCurrentEditingItem', { post: postDetails, drafts });

        } catch (error) {
            console.error(`STORE: Error in fetchPostForEdit for ${postId}:`, error);
            commit('setError', error.message || error.response?.data?.message || `Failed to load item ${postId} for editing.`);
            commit('clearCurrentEditingItem'); // Clear state on error
        } finally {
            commit('setLoading', false);
        }
    },
     // Increments view count via backend (debouncing handled there)
    async incrementViewCount({ commit }, postId) {
        try {
            const response = await api.incrementViewCount(postId); // Assumes api.js maps this
            if (response.status === 201) { // Count was incremented
                 commit('incrementLocalViewCount', postId); 
            }
        } catch (error) {
            console.error(`Error calling increment view count endpoint for post ${postId}:`, error);
        }
    },
    async deleteDraftVersion({ commit }, draftId) {
      try {
        await api.deleteDraftVersion(draftId); // Assumes api.js maps this
        // No mutation needed here, as the list will be refetched by the component
        return true; // Indicate success
      } catch (error) {
        console.error('Error deleting draft version:', error);
        commit('setError', error.response?.data?.message || 'Failed to delete draft version.');
        throw error; // Re-throw for component to handle
      }
    },
    async deletePost({ commit }, id) { // Use 'id' consistently, assuming it's _id
      try {
        await api.deletePost(id) // Assumes api.js maps this
        commit('deletePost', id)
      } catch (error) {
        console.error('Error deleting post:', error)
        throw error
      }
    },
    async addComment({ commit }, { postId, content }) {
      try {
        const comment = await api.addComment(postId, content) // Assumes api.js maps this
        commit('addComment', { postId, comment })
        return comment
      } catch (error) {
        console.error('Error adding comment:', error)
        throw error
      }
    },

    // --- Tag Actions ---
    async fetchTags({ commit }) {
      try {
        const tags = await api.getTags() // Assumes api.js maps this
        commit('setTags', tags)
        return tags
      } catch (error) {
        console.error('Error fetching tags:', error)
        return []
      }
    },
    async searchTags({ commit }, query) {
      try {
        const tags = await api.searchTags(query) // Assumes api.js maps this
        commit('setSuggestedTags', tags)
        return tags
      } catch (error) {
        console.error('Error searching tags:', error)
        return []
      }
    },
    async createTag({ dispatch }, name) {
      try {
        const tag = await api.createTag(name) // Assumes api.js maps this
        await dispatch('fetchTags') // Refresh tag list after creation
        return tag
      } catch (error) {
        console.error('Error creating tag:', error)
        throw error
      }
    },

    // --- Like/Bookmark Actions ---
    async fetchUserLikes({ commit }) {
      try {
        const likes = await api.getUserLikes() // Assumes api.js maps this
        commit('setUserLikes', likes)
        return likes
      } catch (error) {
        console.error('Error fetching user likes:', error)
        return []
      }
    },
    async fetchUserBookmarks({ commit }) {
      try {
        const bookmarks = await api.getUserBookmarks() // Assumes api.js maps this
        commit('setUserBookmarks', bookmarks)
        return bookmarks
      } catch (error) {
        console.error('Error fetching user bookmarks:', error)
        return []
      }
    },
    async toggleLike({ commit, state }, postId) {
      try {
        const isLiked = state.userLikes.includes(postId)
        if (isLiked) {
          await api.unlikePost(postId) // Assumes api.js maps this
          commit('removeUserLike', postId)
        } else {
          await api.likePost(postId) // Assumes api.js maps this
          commit('addUserLike', postId)
        }
        return !isLiked
      } catch (error) {
        console.error('Error toggling like:', error)
        throw error
      }
    },
    async toggleBookmark({ commit, state }, postId) {
      try {
        const isBookmarked = state.userBookmarks.includes(postId)
        if (isBookmarked) {
          await api.unbookmarkPost(postId) // Assumes api.js maps this
          commit('removeUserBookmark', postId)
        } else {
          await api.bookmarkPost(postId) // Assumes api.js maps this
          commit('addUserBookmark', postId)
        }
        return !isBookmarked
      } catch (error) {
        console.error('Error toggling bookmark:', error)
        throw error
      }
    }
    // Note: Removed duplicate/obsolete updatePost and publishPost actions
    // Note: Removed duplicate archivePost action and fixed unused vars in the remaining one
  },
  getters: {
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    // Use _id for MongoDB consistency in getters
    isPostLiked: state => postId => state.userLikes.includes(postId),
    isPostBookmarked: state => postId => state.userBookmarks.includes(postId),
    likeCount: state => postId => {
      if (state.currentPost && state.currentPost._id === postId) {
        return state.currentPost.likes || 0
      }
      const post = state.posts.find(p => p._id === postId)
      return post ? (post.likes || 0) : 0
    },
     // Add getters for editing state
    getCurrentEditingItem: (state) => state.currentEditingItem, 
    getCurrentDraftContent: (state) => state.currentEditingItem?.currentDraft || null,
    getDraftVersions: (state) => state.currentEditingItem?.draftVersions || [],
    // Add getter for loading state
    isLoading: state => state.loading,
    // Add getter for error state
    getError: state => state.error,
    // Add getter for dashboard posts
    getDashboardPosts: state => state.dashboardPosts,
    // Add getter for tags
    getTags: state => state.tags,
  }
})
